"use client";

import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";

import { usePlasmicQueryData, useSelector } from "@stadium/services/plasmic";

import { CUSTOM, DEFAULT, DATA_PROVIDER_STORE_MENU } from "@helpers/plasmic/slices";
import { getProductListingCarouselData } from "@helpers/plasmic/products";

import ProductListingCarousel, {
  ProductListingBaseProps,
} from "@components/swagmagic/dynamic/slices/product-listing-carousel";

interface ProductListingCarouselClientProps extends ProductListingBaseProps {
  setControlContextData?: (data: { slugs: { value: string; label: string }[] }) => void;
  productCount?: number;
}

const ProductListingCarouselClient = (props: ProductListingCarouselClientProps) => {
  const { dynamicSlicesData: providerDynamicSlicesData } = useSelector(DATA_PROVIDER_STORE_MENU);
  const dynamicSlicesData = props?.dynamicSlicesData || providerDynamicSlicesData;
  const { store, menu, allCategories: categories, menuMapping, countryIso } = dynamicSlicesData;
  const { categorySlug, viewType, productCount, identifier, filterOptions } = props;

  const selectedCategory = categorySlug ? menuMapping?.[categorySlug] : undefined;

  if (!isEmpty(categories)) {
    props?.setControlContextData?.({
      slugs: categories?.map((category) => ({
        value: category.code,
        label: capitalize(category.name),
      })),
    });
  }

  const country = { iso: countryIso };

  const { data, isLoading: isProductsLoading } = usePlasmicQueryData(
    [
      "productListingCarousel",
      store,
      menu,
      countryIso,
      menuMapping,
      categorySlug,
      productCount,
      identifier,
      viewType,
      filterOptions,
      selectedCategory,
    ],
    async () => {
      if (!viewType) return null;
      if (viewType === CUSTOM && !identifier) return null;
      if (viewType === DEFAULT && !selectedCategory) return null;

      const { products } = await getProductListingCarouselData({
        dynamicSlicesData,
        categorySlug,
        productCount,
        identifier,
        viewType,
        filterOptions,
        country,
      });

      return { products };
    },
  );

  return (
    <ProductListingCarousel
      {...props}
      dynamicSlicesData={dynamicSlicesData}
      products={data?.products}
      isProductsLoading={isProductsLoading}
      selectedCategory={selectedCategory}
    />
  );
};

export default ProductListingCarouselClient;
